// src/components/Settings/Promotion/Wallet.js
import React, { useEffect, useMemo, useState } from "react";
import "./Wallet.scss";

import coinIcon from "./img/coin.png";
import { getAccessToken } from "../../../awsAuth";
import { coinsToMinor } from "../../../constants/wallet";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

async function fetchJson(url, options) {
  const res = await fetch(url, options);
  const ct = res.headers.get("content-type") || "";
  const isJson = ct.includes("application/json");

  let data;
  if (isJson) {
    try { data = await res.json(); } catch { data = null; }
  } else {
    const raw = await res.text();
    data = { __raw: raw };
  }

  if (!res.ok) {
    const rawMsg =
      (data && data.message) ||
      (data && data.error) ||
      (data && data.__raw && data.__raw.slice(0, 200)) ||
      `HTTP ${res.status}`;
    const err = new Error(`${res.status} ${res.statusText}: ${rawMsg}`);
    err.status = res.status;
    err.url = url;
    err.data = data;
    throw err;
  }

  return data;
}

const Wallet = ({
  isLoggedIn,
  userId,
  balance = 0,
  loading = false,
  onClose,
  onTopup,   // залишив, але тепер не обовʼязково
  onRefresh, // залишив
}) => {
  const [customAmount, setCustomAmount] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [err, setErr] = useState("");

  // ✅ NEW: username того, хто відкрив гаманець
  const [username, setUsername] = useState("");
  const [isTopupOpen, setIsTopupOpen] = useState(false);

  const safeBalance = useMemo(() => {
    const n = Number(balance || 0);
    return Number.isFinite(n) ? n : 0;
  }, [balance]);

  useEffect(() => {
    // refresh balance each time modal opens (best effort)
    try {
      if (onRefresh) onRefresh();
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const quickButtons = [100, 300, 1000];
  const canUse = !!isLoggedIn && !!userId;

  // ✅ NEW: підтягнути username з бекенду (best effort)
  useEffect(() => {
    let alive = true;

    const loadUsername = async () => {
      try {
        if (!canUse) {
          if (alive) setUsername("");
          return;
        }

        // очікуємо, що в тебе є GET /api/account/:id (Users.username)
        const data = await fetchJson(`${API_BASE}/api/account/${encodeURIComponent(String(userId))}`, {
          method: "GET",
        });

        const u =
          data?.username ||
          data?.user?.username ||
          data?.profile?.username ||
          "";

        if (alive) setUsername(String(u || ""));
      } catch {
        if (alive) setUsername("");
      }
    };

    loadUsername();

    return () => {
      alive = false;
    };
  }, [canUse, userId]);

  const createTopupInvoice = async (coins) => {
    const n = Number(coins);
    if (!Number.isFinite(n) || n <= 0) return;

    if (!canUse) {
      setErr("Увійдіть у акаунт, щоб поповнювати гаманець.");
      return;
    }

    setIsCreating(true);
    setErr("");

    try {
      // ✅ старий тестовий onTopup залишаємо як fallback, якщо бекенд недоступний
      // але в нормі тепер йде через monobank
      const amountMinor = coinsToMinor(n);
      const token = await getAccessToken?.().catch(() => null);

      const data = await fetchJson(`${API_BASE}/api/payments/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          purpose: "wallet_topup",
          amountMinor,
          currency: "UAH",
          description: `Поповнення Cvanto Wallet на ${n} ₴`,
          metadata: { userId: String(userId) },
        }),
      });

      const redirectUrl = data?.redirectUrl;
      const paymentId = data?.paymentId;

      if (!redirectUrl) {
        setErr("Не вдалося створити інвойс monobank (немає redirectUrl).");
        return;
      }

      // ✅ збережемо paymentId — потім можна буде робити “сторінку повернення” і опитування статусу
      try {
        if (paymentId) localStorage.setItem("cvanto_last_payment_id", String(paymentId));
      } catch {}

      // ✅ редірект на оплату
      window.location.href = redirectUrl;
    } catch (e) {
      // fallback на стару тест-логіку, якщо хочеш залишити можливість “плюс монетки”
      // (але це тільки якщо ти реально хочеш. Зараз краще вже monobank.)
      // onTopup?.(Math.floor(n));

      setErr(e.message || "Помилка створення платежу");
    } finally {
      setIsCreating(false);
    }
  };

  const handleCustomTopup = () => {
    const n = Number(customAmount);
    if (!Number.isFinite(n) || n <= 0) return;
    createTopupInvoice(Math.floor(n));
    setCustomAmount("");
  };

  const toggleTopup = () => {
    if (!canUse) {
      setErr("Увійдіть у акаунт, щоб поповнювати гаманець.");
      return;
    }
    setErr("");
    setIsTopupOpen((v) => !v);
  };

  return (
    <div className="wallet-overlay" onMouseDown={onClose}>
      <div className="wallet-modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="wallet-close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        {/* ✅ NEW: Card як на скріні */}
        <div className="wallet-card">
          <div className="wallet-card-top">
            <div className="wallet-card-title">Wallet</div>

            <button
              className="wallet-card-plus"
              onClick={toggleTopup}
              disabled={!canUse || loading || isCreating}
              aria-label="Top up"
              title={!canUse ? "Увійдіть, щоб поповнювати" : "Поповнити через monobank"}
            >
              +
            </button>
          </div>

          <div className="wallet-card-name">
            {canUse ? (username || "—") : "Гість"}
          </div>

          <div className="wallet-card-balance">
            {loading ? "..." : safeBalance}
            <span className="wallet-card-currency">₴</span>
          </div>

          <img className="wallet-card-coin" src={coinIcon} alt="coin" />
        </div>

        {/* залишив текст/логіку — але тепер “меню поповнення” відкривається по + */}
        {!canUse && (
          <div className="wallet-warning">
            Увійдіть у акаунт, щоб використовувати гаманець.
          </div>
        )}

        {!!err && (
          <div className="wallet-warning" style={{ borderColor: "rgba(255,120,120,0.45)" }}>
            {err}
          </div>
        )}

        <div className={`wallet-actions ${!canUse ? "disabled" : ""} ${isTopupOpen ? "open" : ""}`}>
          <div className="section-title">Поповнення через monobank (Beta)</div>

          <div className="quick-topup">
            {quickButtons.map((x) => (
              <button
                key={x}
                className="chip"
                onClick={() => createTopupInvoice(x)}
                disabled={!canUse || loading || isCreating}
                title={isCreating ? "Створюємо інвойс..." : `Поповнити на ${x} ₴`}
              >
                {isCreating ? "..." : `+${x}`}
              </button>
            ))}
          </div>

          <div className="custom-topup">
            <input
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              placeholder="Своя сума, напр. 500"
              inputMode="numeric"
              disabled={!canUse || loading || isCreating}
            />
            <button
              onClick={handleCustomTopup}
              disabled={!canUse || loading || isCreating}
              title={isCreating ? "Створюємо інвойс..." : "Перейти до оплати monobank"}
            >
              {isCreating ? "Створення..." : "Поповнити"}
            </button>
          </div>

          <div className="hint">
            1 Token = 1 ₴. Після успішної оплати monobank бекенд збільшить баланс через вебхук.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Wallet;
